/**
 * ShareInfoRemapper
 *
 * Parses Firestore wire JSON into a `ShareInfoDto`.
 *
 * Flutter source:
 *   - lib/data/models/share_info.dart (the Dart model)
 *   - lib/data/models/share_info.g.dart:9-17 (the generated factory)
 *
 * Composes the existing `UsersInfoRemapper.fromJson` and `MediaInfoRemapper.fromJson`
 * for the nested `usersInfo` / `mediaInfo` parses — those remappers are the canonical
 * owners of the corresponding wire-format defaults (e.g. `peerUserId` defaults to `'_'`,
 * `aspectRatio` defaults to `VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO`). Mirrors Flutter's
 * `json['mediaInfo'] == null ? null : MediaInfo.fromJson(...)` guard for the optional
 * nested object.
 */

import type { ShareInfoDto } from '@/data/dtos/share/shareDto';
import type { ShareInfoEntity } from '@/domain/entities/info/shareInfoEntity';
import type { JsonObject } from '@/data/types/json';
import { UsersInfoRemapper } from '@/domain/remappers/info/usersInfoRemapper';
import { MediaInfoRemapper } from '@/domain/remappers/info/mediaInfoRemapper';

export class ShareInfoRemapper {
  static fromJson(data: JsonObject): ShareInfoDto {
    const usersInfoRaw = data['usersInfo'] as JsonObject | undefined | null;
    const mediaInfoRaw = data['mediaInfo'] as JsonObject | undefined | null;

    const shareInfo: ShareInfoDto = {
      type: (data['type'] as number | undefined) ?? 0,
      shareLink: (data['shareLink'] as string | undefined) ?? '',
      // Flutter treats `usersInfo` as a non-nullable nested object; when the wire
      // value is missing, fall through to a zero-value record via UsersInfoRemapper
      // (which applies the Flutter default `peerUserId = '_'`).
      usersInfo: UsersInfoRemapper.fromJson(usersInfoRaw ?? {}),
    };

    if (mediaInfoRaw) {
      shareInfo.mediaInfo = MediaInfoRemapper.fromJson(mediaInfoRaw);
    }

    return shareInfo;
  }

  /**
   * Build a `ShareInfoEntity` from a `ShareInfoDto`.
   *
   * Routes the nested `usersInfo` / `mediaInfo` through their canonical
   * `*Remapper.fromDto` methods so the entity-side widening (e.g.
   * `MediaInfoDto.aspectRatio` / `allowUnlock` being optional but required
   * on the entity with defaults) is applied uniformly.
   */
  static fromDto(dto: ShareInfoDto): ShareInfoEntity {
    const entity: ShareInfoEntity = {
      type: dto.type,
      shareLink: dto.shareLink,
      usersInfo: UsersInfoRemapper.fromDto(dto.usersInfo),
    };
    if (dto.mediaInfo !== undefined) {
      entity.mediaInfo = MediaInfoRemapper.fromDto(dto.mediaInfo);
    }
    return entity;
  }
}
